// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, Stack, Tooltip, CheckCircleIcon, XCloseIcon } from '@metarouter-private/mr-mui';

/**
 * A two-step action button that guards an irreversible bulk change. The primary
 * button swaps into a cancel (✕) / confirm (✓) pair on click, so a single stray
 * click can never mass-mutate the form. The shared confirm pattern for every
 * bulk-apply control.
 */
export default function ConfirmAction({ onConfirm, disabled, label }) {
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = () => {
    setIsConfirming(false);
    onConfirm();
  };

  if (isConfirming) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title="Cancel">
          <IconButton
            color="error"
            size="small"
            onClick={() => setIsConfirming(false)}
            aria-label="Cancel"
            data-test-id="confirmActionCancel"
          >
            <XCloseIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Confirm">
          {/* Re-check validity at confirm time: the underlying form can go invalid
              mid-confirm (e.g. category cleared after entering the confirm step),
              and a stale ✓ would otherwise commit the now-invalid change. */}
          <IconButton
            color="success"
            size="small"
            disabled={disabled}
            onClick={handleConfirm}
            aria-label="Confirm"
            data-test-id="confirmActionConfirm"
          >
            <CheckCircleIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Button
      variant="outlined"
      size="small"
      disabled={disabled}
      onClick={() => setIsConfirming(true)}
      sx={{ whiteSpace: 'nowrap' }}
      data-test-id="confirmActionStart"
    >
      {label}
    </Button>
  );
}

ConfirmAction.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
};

ConfirmAction.defaultProps = {
  disabled: false,
};
